import { inject, Injectable } from '@angular/core';
import {
  AccountService,
  BaseRefreshDataService,
  DecaCompanyStoreService,
  DecaStoreService,
  FreightShipperService,
  UsersStoreService,
} from '@okcargo-apps/shared-application-ionic';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { type InfiniteScrollRefreshSource } from '@okcargo-apps/layout-ionic';

@Injectable({
  providedIn: 'root',
})
export class RefreshDataService extends BaseRefreshDataService {
  private readonly freightShipService = inject(FreightShipperService);
  private readonly accountService = inject(AccountService);
  private readonly usersStoreService = inject(UsersStoreService);

  private readonly decaStoreService = inject(DecaStoreService);
  private readonly decaCompanyStoreService = inject(DecaCompanyStoreService);

  protected override performRefreshAll(): void {
    if (this.accountService.hasCapability(ECapabilities.UPLOAD_FREIGHTS)) {
      this.freightShipService.refreshFreightsShipNotFinished();
      this.freightShipService.refreshFreightsShipFinished();
    }

    this.refreshDocumentsAsShipper('polling');
    this.refreshAvailableCredits();
    this.refreshCompanies();

    // Users are NOT refreshed here (polling / general pull-to-refresh): the query loads on
    // application start (declaratively, keeping data for offline mode) and reloads when the
    // consumer components initialize — the users list and the assignation modal.
    // See `reloadUsersOnComponentInit`.

    // TODO refresh company documents; refresh invoices
  }

  public refreshAllFromUser(): void {
    this.refreshDocumentsAsShipper('user');
    this.refreshAvailableCredits();
    this.refreshCompanies();
  }

  public refreshDocumentsAsShipper(source: 'polling' | 'user'): void {
    if (this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER)) {
      this.decaStoreService.reloadDocumentsAsShipper(source);
    }
  }

  public refreshCurrentDocument(): void {
    if (this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER)) {
      this.decaStoreService.reloadCurrentDocument();
    }
  }

  public refreshAvailableCredits(): void {
    if (this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER)) {
      this.decaStoreService.reloadAvailableCredits();
    }
  }

  public refreshCompanies(): void {
    if (this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER)) {
      this.decaCompanyStoreService.reloadCompanies();
    }
  }

  /** Page-scoped refresh: reloads only the users list. */
  public refreshUsers(source: InfiniteScrollRefreshSource = 'user'): void {
    this.usersStoreService.reloadUsers(source);
  }
}
